// Locale identity across the two conventions that both reach the database.
//
// The `locale` classification axis is seeded country-first with underscores
// (`us_en`, `es_es`, `fr_fr`), while the article and message form defaults are
// language-first with a hyphen (`en-us`). The translation request endpoint
// compares a target locale to the source's `languageIso` with exact string
// equality, and the group's unique index is over the stored strings — so
// offering `us_en` as a target of an `en-us` source would create a SECOND
// US-English variant that neither guard can catch.
//
// Used only to decide which locales are already covered. A requested locale is
// sent as the registry names it: what gets written into a variant's row is a
// data decision, not this surface's to make.

/** Splits on either separator, since the two conventions disagree about which one to use. */
const SEPARATOR = /[-_]/

/**
 * A locale's identity independent of segment order and separator.
 *
 * The two segments are sorted rather than reordered into a canonical
 * language-country form, because knowing which segment is the language needs a
 * language/country table this has no reason to carry. Sorting is total, and over
 * the twelve seeded axis values it collides on nothing (`ca_en` and `ca_fr` stay
 * apart).
 *
 * Anything that is not two segments is lowercased and returned as-is, so an
 * unexpected registry value reads as its own distinct locale instead of
 * throwing inside a picker.
 *
 * @param {string} locale - A locale code in either convention.
 * @returns {string} A key equal for two codes naming the same language and country.
 */
export function localeIdentityKey(locale: string): string {
	const segments = locale.trim().toLowerCase().split(SEPARATOR)
	if (segments.length !== 2) return locale.trim().toLowerCase()
	return segments.sort().join('-')
}

/**
 * Whether two locale codes name the same language and country.
 *
 * @param {string} a - A locale code in either convention.
 * @param {string} b - A locale code in either convention.
 * @returns {boolean} True when both identify one locale.
 */
export function isSameLocale(a: string, b: string): boolean {
	return localeIdentityKey(a) === localeIdentityKey(b)
}

/** Registry labels for the locales the axis knows, keyed so either convention resolves. */
export type LocaleLabels = ReadonlyMap<string, string>

/**
 * Builds the label lookup from the `locale` axis's options.
 *
 * @param {ReadonlyArray<{ code: string; label: string }>} options - The axis's values.
 * @returns {LocaleLabels} Identity key → the registry's label.
 */
export function buildLocaleLabels(options: ReadonlyArray<{ code: string; label: string }>): LocaleLabels {
	return new Map(options.map((option) => [localeIdentityKey(option.code), option.label]))
}

/**
 * A locale's display label — the registry's when it has one.
 *
 * Falls back to the raw code rather than to a prettified guess: a variant may
 * hold a locale the axis does not list, and inventing a label for it would show
 * the user something the registry never said.
 *
 * @param {string} locale - The variant's stored locale code.
 * @param {LocaleLabels} labels - Lookup from {@link buildLocaleLabels}.
 * @returns {string} The label to render.
 */
export function localeLabel(locale: string, labels: LocaleLabels): string {
	return labels.get(localeIdentityKey(locale)) ?? locale
}
